/**
* 10_chandraBhedana_body.tsx
* Body-only version of HTLDSlide10 — no header, no login/logout, no logo.
* Header row (breadcrumb + right-aligned title) and ScaleToFit added to match
* slides 1–9.
*/

import React, { useEffect, useRef, useState } from "react";
import type { CSSProperties } from "react";
import ScaleToFit from "./ScaleToFit";
import SideStarField from "./SideStarField";
import { useIsPortrait } from "./useIsPortrait";
import { FONT_BODY, FONT_DISPLAY, FONT_MONO } from "theme/fonts";
import { pick } from "i18n/lang";
import { localAudio } from "i18n/audio";
import { slide10Content, useSlide10, Seg } from "./slide10.data";

const accent = "#7b8ef0";
const font   = FONT_BODY;

if (typeof document !== "undefined" && !document.getElementById("yogyism-pulse-7b8ef0")) {
 const s = document.createElement("style");
 s.id = "yogyism-pulse-7b8ef0";
 s.textContent = `
   @keyframes yogyismPulse_7b8ef0 {
     0%   { box-shadow: 0 0 0 0 rgba(123,142,240,0.70), 0 0 12px 2px rgba(123,142,240,0.45); transform: scale(1); }
     50%  { box-shadow: 0 0 0 16px rgba(123,142,240,0), 0 0 20px 6px rgba(123,142,240,0.65); transform: scale(1.12); }
     100% { box-shadow: 0 0 0 0 rgba(123,142,240,0), 0 0 12px 2px rgba(123,142,240,0.45); transform: scale(1); }
   }`;
 document.head.appendChild(s);
}

const S: Record<string, CSSProperties> = {
 root:    { background:"#040f18", fontFamily:font, color:"#d0e8f8", width:"100%", height:"100%", display:"flex", flexDirection:"column", boxSizing:"border-box", overflow:"hidden" },
 // Shared header row: breadcrumb (left) + slide title (right), above the divider line
 subtitleBar: {
   background: "#050f1a", borderBottom: "1px solid rgba(123,142,240,0.12)",
   padding: "clamp(5px,0.6vw,9px) clamp(16px,3vw,40px)",
   display: "flex", alignItems: "center", justifyContent: "space-between",
   gap: "clamp(12px,2vw,28px)", flexShrink: 0,
 },
 body:    { flex:1, minHeight:0, display:"flex", gap:"clamp(16px,2vw,28px)", padding:"clamp(10px,1.4vw,18px) clamp(16px,3vw,40px) clamp(10px,1.2vw,14px)", alignItems:"stretch", overflow:"hidden" },
 content: { flex:1, minWidth:"min(100%, 380px)", display:"flex", flexDirection:"column", justifyContent:"space-between" },
 panel:   { width:"clamp(220px,28vw,380px)", flexShrink:0, display:"flex", flexDirection:"column", alignItems:"center", overflow:"hidden" },
};

/* ─── Chandra Guide ──────────────────────────────────────────────────────── */
const ChandraGuideInner: React.FC<{ playing: boolean; onComplete?: () => void; }> = ({ playing, onComplete }) => {
  const canvasRef     = useRef<HTMLCanvasElement>(null);
  const onCompleteRef = useRef(onComplete);
  useEffect(() => { onCompleteRef.current = onComplete; }, [onComplete]);
  
  const st = useRef({
  animId:         0,
  playing:        false,
  round:          0,
  cycleSecs:      12,
  introTimeoutId: 0,
  practiceAudio:  null as HTMLAudioElement | null,
  introAudio:     null as HTMLAudioElement | null,
  drawAnim:       null as (() => void) | null,
  drawStatic:     null as (() => void) | null,
  stopAudio:      null as (() => void) | null,
  startWithIntro: null as (() => void) | null,
  });
  
  useEffect(() => {
  const canvas = canvasRef.current; if (!canvas) return;
  const ctx = canvas.getContext("2d") as CanvasRenderingContext2D; if (!ctx) return;
  const W = 400, H = 560; canvas.width = W; canvas.height = H;
  const cx = 200, cy = 268;
  const R_MIN = 42, R_MAX = 90;
  const TOTAL_ROUNDS = 9;
  const c = pick(slide10Content).canvas; // slides remount on language change
  
  const stars = [[20,18,0.9],[72,10,0.7],[310,16,1.0],[370,40,0.7],[35,65,0.8],[350,60,0.9],[385,8,0.9],[110,42,0.7]];
  function easeInOut(t: number) { return t < 0.5 ? 2*t*t : -1+(4-2*t)*t; }
  
  // ── Audio: intro + single pre-recorded left-nostril cycle ──────────────────
  // chandra-practice.mp3 is ONE full cycle. Looped TOTAL_ROUNDS times; visuals
  // are driven from its currentTime so they never drift.
  const introAudio    = localAudio("/audio/intro-chandra.mp3");    introAudio.preload = "auto";
  const practiceAudio = localAudio("/audio/chandra-practice.mp3"); practiceAudio.preload = "auto";
  st.current.introAudio    = introAudio;
  st.current.practiceAudio = practiceAudio;
  practiceAudio.addEventListener("loadedmetadata", () => {
    if (isFinite(practiceAudio.duration) && practiceAudio.duration > 0)
      st.current.cycleSecs = practiceAudio.duration;
  });
  
  function stopAllAudio() {
    introAudio.onended = null;
    try { introAudio.pause(); introAudio.currentTime = 0; } catch (_) {}
    practiceAudio.onended = null;
    try { practiceAudio.pause(); practiceAudio.currentTime = 0; } catch (_) {}
  }
  
  function drawBg() {
    ctx.clearRect(0, 0, W, H);
    ctx.fillStyle = "#040f18"; ctx.fillRect(0, 0, W, H);
    stars.forEach(([x, y, r]) => { ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI*2); ctx.fillStyle = "rgba(255,255,255,0.50)"; ctx.fill(); });
    ctx.beginPath(); ctx.arc(370,34,16,0,Math.PI*2); ctx.fillStyle="rgba(200,210,255,0.10)"; ctx.fill();
    ctx.beginPath(); ctx.arc(370,34,10,0,Math.PI*2); ctx.fillStyle="rgba(215,225,255,0.18)"; ctx.fill();
    ctx.beginPath(); ctx.arc(370,34, 7,0,Math.PI*2); ctx.fillStyle="#eef0ff"; ctx.fill();
  }
  
  function drawScene(tSec: number, cyc: number, cosmetic: number, roundLabel?: string) {
    const INHALE = (4/12)*cyc, HOLD = (2/12)*cyc, EXHALE = (6/12)*cyc;
    const t = ((tSec % cyc) + cyc) % cyc;
    let phase = "", count = 0, radius = R_MIN, progress = 0;
    if (t < INHALE) {
      phase = c.inhale; progress = t/INHALE; count = Math.max(1, Math.ceil(4 - 4*progress));
      radius = R_MIN + (R_MAX-R_MIN) * easeInOut(progress);
    } else if (t < INHALE+HOLD) {
      phase = c.hold; progress = (t-INHALE)/HOLD; count = Math.max(1, Math.ceil(2 - 2*progress));
      radius = R_MAX;
    } else {
      const ex = t-INHALE-HOLD;
      phase = c.exhale; progress = Math.min(1, ex/EXHALE); count = Math.max(1, Math.ceil(6 - 6*progress));
      radius = R_MAX - (R_MAX-R_MIN) * easeInOut(progress);
    }
  
    const circCY = cy - 40;
    const breathFill = (radius - R_MIN) / (R_MAX - R_MIN);
    const haloR = radius + 70;
    const halo = ctx.createRadialGradient(cx, circCY, radius * 0.4, cx, circCY, haloR);
    halo.addColorStop(0,   `rgba(123,142,240,${(0.18 + 0.22 * breathFill).toFixed(3)})`);
    halo.addColorStop(0.6, `rgba(123,142,240,${(0.06 + 0.08 * breathFill).toFixed(3)})`);
    halo.addColorStop(1,   "rgba(123,142,240,0)");
    ctx.beginPath(); ctx.arc(cx, circCY, haloR, 0, Math.PI*2); ctx.fillStyle = halo; ctx.fill();
    for (let i = 0; i < 5; i++) {
      const rp = ((cosmetic * 0.5 + i * 20) % 100) / 100;
      const rr = radius + 4 + rp * 70;
      const ra = (1 - rp) * (0.14 + 0.18 * breathFill);
      ctx.beginPath(); ctx.arc(cx, circCY, rr, 0, Math.PI*2);
      ctx.strokeStyle = `rgba(123,142,240,${ra.toFixed(3)})`; ctx.lineWidth = 1.2; ctx.stroke();
    }
    const grd = ctx.createRadialGradient(cx - radius*0.3, circCY - radius*0.3, 2, cx, circCY, radius);
    grd.addColorStop(0,    `rgba(200,205,255,${(0.45 + 0.25 * breathFill).toFixed(3)})`);
    grd.addColorStop(0.45, `rgba(123,142,240,${(0.28 + 0.12 * breathFill).toFixed(3)})`);
    grd.addColorStop(1,    "rgba(123,142,240,0.05)");
    ctx.beginPath(); ctx.arc(cx,circCY,radius,0,Math.PI*2); ctx.fillStyle=grd; ctx.fill();
    const sa = (cosmetic * 0.02) % (Math.PI*2);
    ctx.beginPath(); ctx.arc(cx, circCY, radius*0.82, sa, sa + Math.PI*0.6);
    ctx.strokeStyle = `rgba(200,205,255,${(0.18 + 0.18 * breathFill).toFixed(3)})`; ctx.lineWidth = 2; ctx.stroke();
    ctx.strokeStyle = `rgba(123,142,240,${(0.70 + 0.22 * breathFill).toFixed(3)})`;
    ctx.lineWidth = 2.2; ctx.stroke();
    ctx.textAlign="center";
    ctx.font="bold 17px 'JetBrains Mono','Courier New',monospace"; ctx.fillStyle=accent; ctx.fillText(phase, cx, circCY-12, 2*R_MIN-6);
    ctx.font="bold 44px 'JetBrains Mono','Courier New',monospace"; ctx.fillStyle="rgba(123,142,240,0.90)";
    ctx.textBaseline="middle"; ctx.fillText(String(count), cx, circCY+10); ctx.textBaseline="alphabetic";
  
    const nY = cy + 75;
    const lgrd = ctx.createRadialGradient(cx-55,nY,0,cx-55,nY,42);
    lgrd.addColorStop(0,"rgba(123,142,240,0.25)"); lgrd.addColorStop(1,"rgba(123,142,240,0)");
    ctx.beginPath(); ctx.arc(cx-55,nY,42,0,Math.PI*2); ctx.fillStyle=lgrd; ctx.fill();
    ctx.beginPath(); ctx.arc(cx-55,nY,34,0,Math.PI*2); ctx.fillStyle="rgba(123,142,240,0.16)"; ctx.fill();
    ctx.strokeStyle="rgba(123,142,240,0.80)"; ctx.lineWidth=2; ctx.stroke();
    ctx.beginPath(); ctx.arc(cx-55,nY,7,0,Math.PI*2); ctx.fillStyle=accent; ctx.fill();
    ctx.textAlign="center"; ctx.font="bold 12px 'JetBrains Mono','Courier New',monospace"; ctx.fillStyle=accent;
    ctx.fillText(c.left,cx-55,nY+49);
    ctx.font="10px 'JetBrains Mono','Courier New',monospace"; ctx.fillStyle="rgba(123,142,240,0.65)";
    ctx.fillText(c.open,cx-55,nY+62);
  
    ctx.beginPath(); ctx.arc(cx+55,nY,34,0,Math.PI*2); ctx.fillStyle="rgba(123,142,240,0.03)"; ctx.fill();
    ctx.strokeStyle="rgba(123,142,240,0.15)"; ctx.lineWidth=1.2; ctx.stroke();
    ctx.strokeStyle="rgba(123,142,240,0.30)"; ctx.lineWidth=2;
    ctx.beginPath(); ctx.moveTo(cx+42,nY-12); ctx.lineTo(cx+68,nY+12);
    ctx.moveTo(cx+68,nY-12); ctx.lineTo(cx+42,nY+12); ctx.stroke();
    ctx.font="bold 12px 'JetBrains Mono','Courier New',monospace"; ctx.fillStyle="rgba(123,142,240,0.25)";
    ctx.fillText(c.right,cx+55,nY+49);
    ctx.font="10px 'JetBrains Mono','Courier New',monospace"; ctx.fillText(c.closedThumb,cx+55,nY+62);
  
    ctx.beginPath(); ctx.moveTo(cx-22,nY-22); ctx.quadraticCurveTo(cx,nY-55,cx+22,nY-22);
    ctx.strokeStyle="rgba(123,142,240,0.18)"; ctx.lineWidth=1.5; ctx.stroke();
  
    const arcY = nY+90;
    ctx.beginPath(); ctx.arc(cx,arcY,26,-Math.PI/2,-Math.PI/2+Math.PI*2);
    ctx.strokeStyle="rgba(123,142,240,0.08)"; ctx.lineWidth=2.5; ctx.stroke();
    ctx.beginPath(); ctx.arc(cx,arcY,26,-Math.PI/2,-Math.PI/2+progress*Math.PI*2);
    ctx.strokeStyle=accent; ctx.lineWidth=2.5; ctx.stroke();
    ctx.font="bold 32px 'JetBrains Mono','Courier New',monospace"; ctx.fillStyle="rgba(123,142,240,0.88)";
    ctx.textBaseline="middle"; ctx.fillText(String(count),cx,arcY); ctx.textBaseline="alphabetic";
  
    const barY=arcY+48, barX=40, barW=W-80;
    [{l:c.barInhale,w:4},{l:c.barHold,w:2},{l:c.barExhale,w:6}].forEach((seg,i,segs) => {
      const sx=barX+(segs.slice(0,i).reduce((a,s)=>a+s.w,0)/12)*barW;
      const sw=(seg.w/12)*barW;
      const cur=(i===0&&t<INHALE)||(i===1&&t>=INHALE&&t<INHALE+HOLD)||(i===2&&t>=INHALE+HOLD);
      ctx.fillStyle=cur?"rgba(123,142,240,0.28)":"rgba(123,142,240,0.07)"; ctx.fillRect(sx,barY,sw-2,20);
      ctx.strokeStyle=cur?"rgba(123,142,240,0.55)":"rgba(123,142,240,0.12)"; ctx.lineWidth=1; ctx.strokeRect(sx,barY,sw-2,20);
      ctx.font="10px 'JetBrains Mono','Courier New',monospace"; ctx.fillStyle=cur?accent:"rgba(123,142,240,0.30)";
      ctx.fillText(seg.l,sx+(sw-2)/2,barY+13);
    });
  
    ctx.font="15px 'Space Grotesk',Arial,sans-serif"; ctx.fillStyle="rgba(123,142,240,0.32)";
    ctx.textAlign="center";
    ctx.fillText(roundLabel ?? c.caption, cx, barY+38);
  }
  
  function drawStatic() {
    drawBg(); drawScene(0, st.current.cycleSecs, 0);
    ctx.fillStyle = "rgba(4,15,24,0.40)"; ctx.fillRect(0, 0, W, H);
    ctx.textAlign = "center"; ctx.font = "14px 'Space Grotesk',Arial,sans-serif";
    ctx.fillStyle = "rgba(123,142,240,0.38)"; ctx.fillText(c.pressToBegin, cx, H - 16);
  }
  
  function drawAnim() {
    if (!st.current.playing) return;
    drawBg();
    const a    = st.current.practiceAudio;
    const cyc  = st.current.cycleSecs || 12;
    const tSec = a ? a.currentTime : 0;
    const currentRound = Math.min(st.current.round + 1, TOTAL_ROUNDS);
    drawScene(tSec, cyc, performance.now() / 16, c.round(currentRound, TOTAL_ROUNDS));
    st.current.animId = requestAnimationFrame(drawAnim);
  }
  
  function beginPractice() {
    clearTimeout(st.current.introTimeoutId);
    if (st.current.playing) return;
    const a = st.current.practiceAudio;
    if (!a) return;
    st.current.playing = true;
    st.current.round   = 0;
    a.currentTime = 0;
    a.onended = () => {
      st.current.round++;
      if (st.current.round >= TOTAL_ROUNDS) {
        st.current.playing = false;
        stopAllAudio();
        drawStatic();
        onCompleteRef.current?.();
      } else {
        a.currentTime = 0;
        a.play().catch(() => {});
      }
    };
    a.play().catch(() => {});
    st.current.drawAnim?.();
  }
  
  function startWithIntro() {
    const intro = st.current.introAudio;
    if (!intro) { beginPractice(); return; }
    intro.onended = () => {
      intro.onended = null;
      st.current.introTimeoutId = window.setTimeout(beginPractice, 1000);
    };
    intro.currentTime = 0;
    intro.play().then(() => {
      const dur = intro.duration;
      const ms  = isFinite(dur) ? Math.ceil(dur * 1000) + 2000 : 30000;
      st.current.introTimeoutId = window.setTimeout(beginPractice, ms);
    }).catch(beginPractice);
  }
  
  st.current.drawAnim       = drawAnim;
  st.current.drawStatic     = drawStatic;
  st.current.stopAudio      = stopAllAudio;
  st.current.startWithIntro = startWithIntro;
  drawStatic();
  
  const cur = st.current;
  return () => {
    cancelAnimationFrame(cur.animId);
    clearTimeout(cur.introTimeoutId);
    stopAllAudio();
  };
  }, []); // eslint-disable-line
  
  useEffect(() => {
  const s = st.current;
  cancelAnimationFrame(s.animId);
  clearTimeout(s.introTimeoutId);
  s.playing = false;
  s.stopAudio?.();
  if (playing) { s.startWithIntro?.(); } else { s.drawStatic?.(); }
  }, [playing]);
  
  return (
  <canvas ref={canvasRef} style={{ display: "block", borderRadius: 8, width: "100%", height: "100%", objectFit: "contain" }} />
  );
  };

const ChandraGuide:React.FC=()=>{
 const[playing,setPlaying]=useState(false);
 const audioCtx=useRef<AudioContext|null>(null);const masterGain=useRef<GainNode|null>(null);const oscs=useRef<OscillatorNode[]>([]);const lfo=useRef<OscillatorNode|null>(null);const binOscs=useRef<OscillatorNode[]>([]);const binGain=useRef<GainNode|null>(null);
 const startAudio=()=>{const ctx=new(window.AudioContext||(window as any).webkitAudioContext)();audioCtx.current=ctx;const gain=ctx.createGain();gain.gain.setValueAtTime(0,ctx.currentTime);gain.gain.linearRampToValueAtTime(0.04,ctx.currentTime+2.0);gain.connect(ctx.destination);masterGain.current=gain;const delay=ctx.createDelay(1.0);const fbG=ctx.createGain();delay.delayTime.value=0.45;fbG.gain.value=0.35;delay.connect(fbG);fbG.connect(delay);delay.connect(gain);oscs.current=([[98,0.55],[147,0.28],[196,0.14]] as[number,number][]).map(([freq,vol])=>{const osc=ctx.createOscillator();const og=ctx.createGain();osc.type="sine";osc.frequency.value=freq;og.gain.value=vol;osc.connect(og);og.connect(delay);og.connect(gain);osc.start();return osc;});const lfoNode=ctx.createOscillator();const lfoG=ctx.createGain();lfoNode.frequency.value=0.067;lfoG.gain.value=0;lfoNode.connect(lfoG);lfoG.connect(gain.gain);lfoNode.start();lfo.current=lfoNode;const merger=ctx.createChannelMerger(2);const bGain=ctx.createGain();bGain.gain.setValueAtTime(0,ctx.currentTime);bGain.gain.linearRampToValueAtTime(0,ctx.currentTime+3.5);merger.connect(bGain);bGain.connect(ctx.destination);binGain.current=bGain;const lOsc=ctx.createOscillator();const lG=ctx.createGain();lOsc.type="sine";lOsc.frequency.value=180;lG.gain.value=1;lOsc.connect(lG);lG.connect(merger,0,0);lOsc.start();const rOsc=ctx.createOscillator();const rG=ctx.createGain();rOsc.type="sine";rOsc.frequency.value=184;rG.gain.value=1;rOsc.connect(rG);rG.connect(merger,0,1);rOsc.start();binOscs.current=[lOsc,rOsc];};
 const stopAudio=()=>{if(!audioCtx.current)return;const ctx=audioCtx.current;masterGain.current?.gain.linearRampToValueAtTime(0,ctx.currentTime+1.2);binGain.current?.gain.linearRampToValueAtTime(0,ctx.currentTime+1.2);setTimeout(()=>{[...oscs.current,...binOscs.current].forEach(o=>{try{o.stop();}catch(_){}});try{lfo.current?.stop();}catch(_){}ctx.close().catch(()=>{});audioCtx.current=masterGain.current=lfo.current=binGain.current=null;oscs.current=binOscs.current=[];},1300);};
 useEffect(()=>()=>{[...oscs.current,...binOscs.current].forEach(o=>{try{o.stop();}catch(_){}});try{lfo.current?.stop();}catch(_){}audioCtx.current?.close().catch(()=>{});},[]);
 useEffect(()=>{const stop=()=>{setPlaying(false);stopAudio();};window.addEventListener("yogyism:stop",stop);return()=>window.removeEventListener("yogyism:stop",stop);},[]);// eslint-disable-line
 const handleToggle=()=>{const next=!playing;setPlaying(next);if(next){startAudio();window.dispatchEvent(new Event("yogyism:play"));}else{stopAudio(); }};
 return(
   <div style={{display:"flex",flexDirection:"column",alignItems:"center",width:"100%",height:"100%"}}>
     <div style={{flexShrink:0,marginBottom:"clamp(4px,0.5vw,6px)"}}>
       <button onClick={handleToggle} style={{width:32,height:32,display:"flex",alignItems:"center",justifyContent:"center",background:playing?"rgba(123,142,240,0.08)":"rgba(123,142,240,0.32)",border:`1px solid rgba(123,142,240,${playing?0.32:0.85})`,borderRadius:"50%",cursor:"pointer",color:playing?"rgba(123,142,240,0.60)":accent,transition:"all 0.2s",animation:playing?"none":"yogyismPulse_7b8ef0 1.3s ease-in-out infinite"}} onMouseEnter={e=>(e.currentTarget.style.background="rgba(123,142,240,0.28)")} onMouseLeave={e=>(e.currentTarget.style.background=playing?"rgba(123,142,240,0.08)":"rgba(123,142,240,0.20)")}>
         {playing?<svg width="10" height="10" viewBox="0 0 10 10" fill="currentColor"><rect x="0" y="0" width="10" height="10" rx="1"/></svg>:<svg width="9" height="10" viewBox="0 0 9 10" fill="currentColor"><polygon points="0,0 9,5 0,10"/></svg>}
       </button>
     </div>
     <div style={{flex:1,minHeight:0,width:"100%",overflow:"hidden",display:"flex",alignItems:"center",justifyContent:"center"}}>
       <ChandraGuideInner playing={playing} onComplete={() => {
   setPlaying(false);
   stopAudio();
   // Natural finish (not user-stop) → advance the deck to the next slide
   window.dispatchEvent(new Event("yogyism:next"));
 }}/>
     </div>
   </div>
 );
};

const lbl:CSSProperties={fontSize:"clamp(8px,0.7vw,11px)",fontFamily:FONT_MONO,color:accent,letterSpacing:3,textTransform:"uppercase",marginBottom:"clamp(4px,0.55vw,8px)",fontStyle:"normal"};
const txt:CSSProperties={fontSize:"clamp(12px,1.0vw,15px)",fontStyle:"normal",fontFamily:FONT_BODY,lineHeight:1.7,color:"#cde0f2",margin:0};

const renderSegs = (segs: Seg[], boldColor: string) =>
  segs.map((s, i) =>
    s.b ? <span key={i} style={{ color: boldColor, fontWeight: 700 }}>{s.t}</span>
    : s.em ? <em key={i}>{s.t}</em>
    : <React.Fragment key={i}>{s.t}</React.Fragment>
  );

const ChandraBhedanaBodyInner:React.FC=()=>{
 const { meta, steps, why, threshold } = useSlide10();
 const fontLinkRef = useRef<HTMLLinkElement | null>(null);
 useEffect(() => {
   const existing = document.querySelector('link[href*="Space+Grotesk"]') as HTMLLinkElement | null;
   if (!existing) {
     const link = document.createElement("link");
     link.rel  = "stylesheet";
     link.href = "https://fonts.googleapis.com/css2?family=Merriweather:ital,wght@0,300;0,400;0,700;1,300;1,400;1,700&display=swap";
     document.head.appendChild(link);
     fontLinkRef.current = link;
   }
   return () => {
     if (fontLinkRef.current && document.head.contains(fontLinkRef.current)) {
       document.head.removeChild(fontLinkRef.current); fontLinkRef.current = null;
     }
   };
 }, []);

 const rootRef = useRef<HTMLDivElement>(null);

 return (
<div style={S.root} ref={rootRef}>      {/* add ref={rootRef} */}
<SideStarField rootRef={rootRef} />   {/* add this line */}

     {/* Header row: breadcrumb (left) + slide title (right), above the horizontal line */}
     <div style={S.subtitleBar}>
       <div style={{
         fontSize: "clamp(11px,1.0vw,14px)", fontStyle: "normal", color: "#7aa8cc",
         fontFamily: FONT_BODY,
       }}>
         {meta.breadcrumbPre} &nbsp;·&nbsp;{" "}
         <span style={{ color: accent }}>{meta.breadcrumbAccent}</span>
       </div>
       <div style={{
         fontSize: "clamp(16px,1.9vw,28px)", fontStyle: "normal", fontWeight: 600,
         color: "#e8f4ff", fontFamily: FONT_DISPLAY, letterSpacing: 0.5, lineHeight: 1,
         whiteSpace: "nowrap",
       }}>
         {meta.headerTitlePre}<span style={{ color: accent }}>{meta.headerTitleAccent}</span>
       </div>
     </div>

     <div style={S.body}>
       <div style={S.content}>
         <div>
           <div style={{fontSize:"clamp(8px,0.68vw,11px)",fontFamily:FONT_MONO,color:"rgba(123,142,240,0.52)",letterSpacing:3,marginBottom:"clamp(4px,0.5vw,7px)"}}>{meta.sanskrit}</div>
           <h1 style={{fontFamily:FONT_DISPLAY,fontSize:"clamp(19px,2.3vw,30px)",fontStyle:"normal",fontWeight:600,color:"#e8f4ff",margin:"0 0 clamp(2px,0.35vw,6px)",lineHeight:1.2}}>{meta.titlePre}<span style={{color:accent}}>{meta.titleAccentWord}</span>{meta.titlePost}</h1>
           <p style={{fontFamily:FONT_BODY,fontSize:"clamp(12px,1.1vw,15px)",fontStyle:"normal",fontWeight:300,color:"#7aa8cc",margin:0,letterSpacing:0.3}}>{meta.subtitle}</p>
         </div>
         <div style={{borderBottom:"1px solid rgba(123,142,240,0.10)",paddingBottom:"clamp(7px,0.9vw,12px)"}}>
           <div style={lbl}>{meta.whyLabel}</div>
           <p style={txt}>{renderSegs(why, "#c8ccff")}</p>
         </div>
         <div style={{borderBottom:"1px solid rgba(123,142,240,0.10)",paddingBottom:"clamp(7px,0.9vw,12px)"}}>
           <div style={lbl}>{meta.howLabel}</div>
           <div style={{display:"flex",flexDirection:"column",gap:"clamp(5px,0.7vw,9px)"}}>
             {steps
               .map((item,i)=>(
               <div key={i} style={{display:"flex",gap:"clamp(8px,1vw,14px)",alignItems:"flex-start"}}>
                 <div style={{width:7,height:7,borderRadius:"50%",background:accent,flexShrink:0,marginTop:6,opacity:0.7}}/>
                 <div><span style={{fontFamily:FONT_BODY,fontSize:"clamp(11px,0.92vw,14px)",fontStyle:"normal",fontWeight:700,color:"#c0c8ff"}}>{item.step}&nbsp;</span><span style={{fontFamily:FONT_BODY,fontSize:"clamp(11px,0.92vw,14px)",fontStyle:"normal",fontWeight:300,color:"#9bbdd6"}}>{item.desc}</span></div>
               </div>
             ))}
           </div>
         </div>
         <div style={{background:"rgba(123,142,240,0.06)",border:"1px solid rgba(123,142,240,0.22)",borderRadius:8,padding:"clamp(11px,1.3vw,18px) clamp(13px,1.5vw,20px)"}}>
           <div style={lbl}>{meta.thresholdLabel}</div>
           <p style={{...txt,color:"#c8ccff"}}>{renderSegs(threshold, "#e0e4ff")}</p>
         </div>
       </div>

       {/* Panel — chandra guide fills the full panel height */}
       <div style={S.panel}><ChandraGuide/></div>
     </div>
   </div>
 );
};

const ChandraBhedanaMobile: React.FC = () => {
  const { meta } = useSlide10();
  return (
  <div style={{ background:"#040f18", color:"#d0e8f8", fontFamily:font, height:"100%", width:"100%", boxSizing:"border-box", display:"flex", flexDirection:"column", padding:"18px 16px 20px", overflow:"hidden" }}>
    <div style={{ flexShrink:0, textAlign:"center", marginBottom:10 }}>
      <div style={{ fontFamily:FONT_MONO, fontSize:10, letterSpacing:2, color:"rgba(123,142,240,0.7)", textTransform:"uppercase", marginBottom:4 }}>{meta.breadcrumbPre}</div>
      <h1 style={{ fontFamily: FONT_DISPLAY, fontStyle:"normal", fontWeight:600, fontSize:22, margin:0, color:"#e8f4ff" }}>{meta.headerTitlePre}<span style={{color:accent}}>{meta.headerTitleAccent}</span></h1>
      <p style={{ fontFamily: FONT_BODY, fontStyle:"normal", fontWeight:300, fontSize:12.5, color:"#7aa8cc", margin:"3px 0 0" }}>{meta.guideSummary}</p>
    </div>
    <div style={{ flex:1, minHeight:0, width:"100%", display:"flex", alignItems:"center", justifyContent:"center", overflow:"hidden" }}>
      <ChandraGuide />
    </div>
  </div>
  );
};

// Scale the whole slide as one unit so it looks identical across screen
// resolutions, window sizes, and browser zoom — no reflow, no clipping.
const ChandraBhedanaBody: React.FC = () => {
  const portrait = useIsPortrait();
  return portrait
    ? <ChandraBhedanaMobile />
    : <ScaleToFit><ChandraBhedanaBodyInner /></ScaleToFit>;
};

export default ChandraBhedanaBody;